import { useEffect, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, Text } from 'react-native'

import { useNavigation, useRoute } from '@react-navigation/native'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import {
  getUserFlowSession,
  setUserFlowRoute,
} from '@zet/utils/userFlowSession'

import ScreenViewEventWrapper from '../../components/screenViewEventWrapper'
import {
  UserFlowLoading,
  UserFlowRecommendationCard,
} from '../../components/userFlow'
import { useUserFlowRecommendations } from '../userFlow/utils'

type UserFlowOfferRouteParams = {
  customerId?: number
}

const UserOffers = () => {
  const route = useRoute() as { params?: UserFlowOfferRouteParams }
  const navigation = useNavigation()
  const session = getUserFlowSession()
  const customerId = route?.params?.customerId || session?.customerId
  const [showAllOffers, setShowAllOffers] = useState(false)
  const {
    isLoading,
    customerId: resolvedCustomerId,
    freshProducts,
    totalProductCount,
    paginationMeta,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUserFlowRecommendations(Number(customerId))
  const handleUserOffersApplySuccess = () => {
    navigation.replace(
      'UserHome' as never,
      { customerId: resolvedCustomerId } as never,
    )
  }

  const visibleProducts = showAllOffers
    ? freshProducts
    : freshProducts?.slice(0, 3)

  useEffect(() => {
    setUserFlowRoute('UserOffers')
  }, [])

  useEffect(() => {
    if (!customerId) {
      navigation.replace('UserLogin')
    }
  }, [customerId, navigation])

  if (isLoading) {
    return <UserFlowLoading />
  }

  return (
    <ScreenViewEventWrapper screenName="user_flow_offers">
      <ScrollView
        className="flex-1 bg-white"
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Stack className="px-4 pb-6 pt-9">
          <Text style={styles.screenTitle}>
            {`${paginationMeta?.total || totalProductCount || freshProducts?.length || 0} Loan Offers for You`}
          </Text>
        </Stack>
        <Stack style={styles.cardsContainer}>
          {visibleProducts?.map((item, index) => (
            <Stack
              key={`${item?.partnerId}-${item?.id}-${index}`}
              style={[
                styles.cardWrapper,
                index === visibleProducts?.length - 1 && styles.lastCardWrapper,
              ]}
            >
              <UserFlowRecommendationCard
                product={item}
                customerId={resolvedCustomerId}
                onApplySuccess={handleUserOffersApplySuccess}
                index={index}
              />
            </Stack>
          ))}

          {(totalProductCount || 0) > 3 || (freshProducts?.length || 0) > 3 ? (
            <Pressable
              onPress={() => {
                if (!showAllOffers && hasNextPage && !isFetchingNextPage) {
                  fetchNextPage()
                }
                setShowAllOffers((currentValue) => !currentValue)
              }}
            >
              <XStack style={styles.toggleContainer}>
                {isFetchingNextPage ? (
                  <ActivityIndicator
                    size="small"
                    color="#832DC2"
                    style={styles.loader}
                  />
                ) : (
                  <>
                    <Text style={styles.toggleText}>
                      {showAllOffers ? 'View Less' : 'View All'}
                    </Text>
                    <Icon
                      type="ArrowDown"
                      size={24}
                      color="#832DC2"
                      style={[
                        styles.toggleIcon,
                        showAllOffers && styles.toggleIconExpanded,
                      ]}
                    />
                  </>
                )}
              </XStack>
            </Pressable>
          ) : null}
        </Stack>
      </ScrollView>
    </ScreenViewEventWrapper>
  )
}

const styles = StyleSheet.create({
  cardsContainer: {
    paddingHorizontal: 16,
  },
  cardWrapper: {
    marginBottom: 20,
  },
  lastCardWrapper: {
    marginBottom: 0,
  },
  loader: {
    marginTop: 18,
  },
  screenTitle: {
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 20,
    lineHeight: 26,
    letterSpacing: -0.4,
    color: '#0C0E11',
  },
  scrollContent: {
    paddingBottom: 40,
    paddingTop: 40,
  },
  toggleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
  },
  toggleIcon: {
    marginLeft: 4,
    transform: [{ rotate: '0deg' }],
  },
  toggleIconExpanded: {
    transform: [{ rotate: '180deg' }],
  },
  toggleText: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 14,
    lineHeight: 24,
    letterSpacing: -0.28,
    color: '#832DC2',
  },
})

export default UserOffers
